import React, { useMemo, useState } from 'react';
import moment from 'moment';

import type Trip from '@this/domain/trip/trip2';
import { styled } from '@this/src/components/constants/themes';
import { getSpacing } from '@this/src/components/shared/ui/theme';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Box } from '@material-ui/core';
import OrganizationModal from './organization_modal';

type TripUserInfo = {
  name: string;
  name_roman: string;
  birthday: string;
  age: number | null;
  gender: string;
  tel: string;
  email: string;
  domestic_approval_required: boolean;
  foreign_approval_required: boolean;
  department: { name: string };
  grade: { name: string };
};

interface Props {
  trip: Trip;
  planName: string;
}
export const PartnerDescription: React.FC<Props> = ({ trip, planName }) => {
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);
  // 1つ目のorder_itemの１つ目のtraveler_informationにuserがあれば、それが出張代表者
  // なければログインユーザが出張代表者
  const travelerInfo = useMemo(() => {
    if (!trip.currentOrder || !trip.currentOrder.orderItems) return null;
    if (trip.currentOrder.orderItems[0] === undefined) return null;
    if (trip.currentOrder.orderItems[0].travelerInformations === undefined) return null;
    if (trip.currentOrder.orderItems[0].travelerInformations[0] === undefined) return null;
    return trip.currentOrder.orderItems[0].travelerInformations[0].user;
  }, [trip]);

  const userInfo = useMemo<TripUserInfo>(() => {
    const birthday = trip.user.birthday;

    return {
      name: trip.user.name,
      name_roman: `${trip.user.lastNameRoman} ${trip.user.firstNameRoman}`,
      birthday: birthday ? birthday.format('YYYY-MM-DD') : '',
      age: moment().diff(moment(birthday, 'YYYY-MM-DD'), 'years'),
      gender: trip.user.gender ?? '',
      tel: trip.user.nationalizedTel ?? '',
      email: trip.user.email ?? '',
      domestic_approval_required: trip.user.domesticApprovalRequired ?? false,
      foreign_approval_required: trip.user.foreignApprovalRequired ?? false,
      department: {
        name: utils.dig(trip, 'user', 'department', 'name') ?? ''
      },
      grade: {
        name: utils.dig(trip, 'user', 'grade', 'name') ?? ''
      }
    };
  }, [trip]);

  const organizationId = trip?.user?.organization?.id;

  return (
    <div>
      <Section>
        <Box display="flex" justifyContent="space-between">
          <div>
            <SectionTitle>料金プラン</SectionTitle>
            <p>{planName}</p>
          </div>
          {organizationId && (
            <Section>
              <Button onClick={() => setShowOrganizationModal(true)}>企業情報</Button>
              <OrganizationModal
                show={showOrganizationModal}
                organizationId={organizationId}
                onClose={() => setShowOrganizationModal(false)}
              />
            </Section>
          )}
        </Box>
      </Section>
      <Section>
        <SectionTitle>予約者</SectionTitle>
        <ul>
          <li>氏名: {userInfo.name}</li>
          <li>ローマ字: {userInfo.name_roman}</li>
          <li>生年月日: {`${userInfo.birthday}(${userInfo.age}歳)`}</li>
          <li>性別: {userInfo.gender}</li>
          <li>Tel: {userInfo.tel}</li>
          <li>E-mail: {userInfo.email}</li>
          <li>部署: {userInfo.department.name}</li>
          <li>国内承認: {userInfo.domestic_approval_required ? 'あり' : 'なし'}</li>
          <li>海外承認: {userInfo.foreign_approval_required ? 'あり' : 'なし'}</li>
        </ul>
      </Section>

      <Section>
        <SectionTitle>旅費規程適用ユーザ(出張代表者)情報</SectionTitle>

        {travelerInfo ? (
          <ul>
            <li>氏名: {travelerInfo.name}</li>
            <li>ローマ字: {`${travelerInfo.lastNameRoman} ${travelerInfo.firstNameRoman}`}</li>
            <li>
              生年月日:{' '}
              {travelerInfo.birthday
                ? `${moment(travelerInfo.birthday).format('YYYY-MM-DD')}(${moment().diff(
                    moment(travelerInfo.birthday),
                    'years'
                  )}歳)`
                : ''}
            </li>
            <li>性別: {travelerInfo.gender}</li>
            <li>Tel: {travelerInfo.nationalizedTel}</li>
            <li>E-mail: {travelerInfo.email}</li>
            <li>役職等級: {utils.dig(travelerInfo, 'grade', 'name') || ''}</li>
            <li>部署: {utils.dig(travelerInfo, 'department', 'name') || ''}</li>
            <li>国内承認: {travelerInfo.domesticApprovalRequired ? 'あり' : 'なし'}</li>
            <li>海外承認: {travelerInfo.foreignApprovalRequired ? 'あり' : 'なし'}</li>
          </ul>
        ) : (
          <ul>
            <li>氏名: {userInfo.name}</li>
            <li>ローマ字: {userInfo.name_roman}</li>
            <li>生年月日: {`${userInfo.birthday}(${userInfo.age}歳)`}</li>
            <li>性別: {userInfo.gender}</li>
            <li>Tel: {userInfo.tel}</li>
            <li>E-mail: {userInfo.email}</li>
            <li>部署: {userInfo.department.name}</li>
            <li>国内承認: {userInfo.domestic_approval_required ? 'あり' : 'なし'}</li>
            <li>海外承認: {userInfo.foreign_approval_required ? 'あり' : 'なし'}</li>
          </ul>
        )}
      </Section>
    </div>
  );
};

const Section = styled.section`
  & + & {
    margin-top: ${getSpacing(4)}px;
  }
`;

const SectionTitle = styled.h3`
  margin-bottom: ${getSpacing(1)}px;
`;
